import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import { ROUTES } from '@shared/constants/routes';
import { easeStripe } from '../../lib/motion';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';
import { AUDIENCE_NAV } from '../../config/audience-nav';

const ASSURANCES = [
  'Sans inscription',
  'Aucune relance commerciale',
  'Estimation chiffrée',
  'Sources officielles citées',
];

export function FinalImmersiveCTA() {
  const { effective } = useEffectiveAudience();
  const primary = effective
    ? AUDIENCE_NAV[effective.id].primaryCta
    : { label: 'Voir nos outils gratuits', href: ROUTES.outils };

  return (
    <section aria-labelledby="final-cta-title" className="relative bg-paper pb-4 pt-12 md:pt-20">
      <Container>
        <div className="relative overflow-hidden rounded-[36px] bg-ink px-6 py-14 sm:px-12 sm:py-16 md:px-16 md:py-20">
          {/* Mesh background décoratif */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-mesh-dark opacity-90"
          />
          {/* Halo principal */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-32 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/30 blur-[140px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -left-24 bottom-[-120px] h-[420px] w-[420px] rounded-full bg-brand-500/30 blur-[120px]"
          />
          {/* Grid pattern */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 80%)',
            }}
          />
          {/* Noise overlay */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-noise opacity-[0.04]"
          />

          <div className="relative grid gap-10 lg:grid-cols-[1.4fr,1fr] lg:items-center lg:gap-16">
            {/* Colonne gauche — texte */}
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory-50 backdrop-blur-md">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-glow-pulse rounded-full bg-accent-400/70" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                  </span>
                  Gratuit, sans engagement
                </span>

                <h2 id="final-cta-title" className="h-hero on-dark mt-5">
                  Combien votre projet{' '}
                  <span className="bg-gradient-to-br from-accent-300 via-brand-300 to-brand-200 bg-clip-text text-transparent">
                    peut-il vous rapporter ?
                  </span>
                </h2>

                <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-ivory-50/70">
                  Décrivez votre situation en quelques clics. On identifie les aides auxquelles vous
                  avez droit et on chiffre votre estimation — immédiatement, sans quitter cette
                  page.
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-2.5">
                  <Button asChild size="xl" variant="accent" className="halo-accent group">
                    <a href={primary.href}>
                      {primary.label}
                      <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                  <Button asChild size="xl" variant="dark-outline" className="group">
                    <a href={ROUTES.contact}>
                      Échanger avec un expert
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                </div>

                {/* Réassurances */}
                <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-ivory-50/70">
                  {ASSURANCES.map((a) => (
                    <li key={a} className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-accent-400" strokeWidth={2.4} />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Colonne droite — preview "diagnostic" mock */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.15, ease: easeStripe }}
              className="relative hidden lg:block"
            >
              <div
                aria-hidden
                className="absolute -inset-3 -z-10 rounded-3xl bg-gradient-to-br from-accent-400/20 via-brand-400/0 to-brand-500/30 blur-2xl"
              />
              <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-stone-900/60 p-5 shadow-floating backdrop-blur-xl">
                {/* Header */}
                <div className="flex items-center gap-2 border-b border-white/10 pb-3">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-stone-700" />
                    <span className="h-2 w-2 rounded-full bg-stone-700" />
                    <span className="h-2 w-2 rounded-full bg-stone-700" />
                  </div>
                  <p className="ml-1 font-mono text-[10px] text-ivory-50/50">
                    estimation / résultats
                  </p>
                </div>
                {/* Mock content */}
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory-50/40">
                        Profil
                      </p>
                      <p className="mt-0.5 text-[12px] text-ivory-50">Tertiaire, 1 200 m²</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory-50/40">
                        Région
                      </p>
                      <p className="mt-0.5 text-[12px] text-ivory-50">Île-de-France</p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/5 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory-50/40">
                      Estimation prime CEE
                    </p>
                    <p className="mt-1 font-display text-2xl font-bold text-ivory-50">
                      <span className="bg-gradient-to-br from-accent-300 to-brand-300 bg-clip-text text-transparent">
                        12 400 €
                      </span>
                      <span className="ml-1.5 text-sm font-normal text-ivory-50/50">
                        – 18 600 €
                      </span>
                    </p>
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-accent-500/20 px-1.5 py-0.5 text-[9.5px] font-semibold text-accent-300">
                        <Sparkles className="h-2 w-2" />4 aides éligibles
                      </span>
                      <span className="inline-flex items-center rounded-md bg-brand-500/20 px-1.5 py-0.5 text-[9.5px] font-semibold text-brand-200">
                        Primes énergie
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Pompe à chaleur air/eau', pct: 78 },
                      { label: 'Isolation toiture', pct: 62 },
                      { label: 'Pilotage bâtiment', pct: 91 },
                    ].map((r, i) => (
                      <div key={r.label}>
                        <div className="flex items-center justify-between text-[10.5px]">
                          <span className="text-ivory-50/70">{r.label}</span>
                          <span className="font-mono font-semibold text-ivory-50">{r.pct}%</span>
                        </div>
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/10">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${r.pct}%` }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.8,
                              delay: 0.4 + i * 0.1,
                              ease: easeStripe,
                            }}
                            className="h-full rounded-full bg-gradient-to-r from-brand-400 to-accent-400"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: 0.4, ease: easeStripe }}
                className="absolute -right-3 -top-3 z-10"
              >
                <div className="flex items-center gap-1.5 rounded-full border border-accent-300/30 bg-accent-500/20 px-2.5 py-1 backdrop-blur-md">
                  <Sparkles className="h-3 w-3 text-accent-300" />
                  <p className="text-[10.5px] font-semibold text-accent-200">Aperçu en direct</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
}
